import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Text } from 'roo-ui/components';
import PillButton from 'components/PillButton';
import { setFilters } from 'store/propertyAvailability/propertyAvailabilityActions';
import {
  getAvailableClassicRewardCount,
  getAvailableDepositPayCount,
  getAvailableFreeCancellationCount,
  getClassicRewardAvailabilityMessage,
  getDepositPayAvailabilityMessage,
  getFilters,
  getIsLoading,
} from 'store/propertyAvailability/propertyAvailabilitySelectors';

const FadeInWrapper = styled(Flex)`
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
`;

const FilterMenu = () => {
  const dispatch = useDispatch();
  const filters = useSelector(getFilters);
  const isLoading = useSelector(getIsLoading);
  const classicRewardsCount = useSelector(getAvailableClassicRewardCount);
  const depositPayCount = useSelector(getAvailableDepositPayCount);
  const freeCancellationCount = useSelector(getAvailableFreeCancellationCount);
  const classicRewardsMessaging = useSelector(getClassicRewardAvailabilityMessage);
  const depositPayMessaging = useSelector(getDepositPayAvailabilityMessage);

  const onToggleFilter = useCallback(
    (filterName) => {
      const previousValue = filters[filterName];
      const nextValue = !previousValue;

      dispatch(setFilters({ [filterName]: nextValue }));
    },
    [dispatch, filters],
  );

  return (
    <UnconnectedFilterMenu
      classicRewardsCount={classicRewardsCount}
      depositPayCount={depositPayCount}
      freeCancellationCount={freeCancellationCount}
      filters={filters}
      filterMessage={depositPayMessaging || classicRewardsMessaging}
      isLoading={isLoading}
      onToggleFilter={onToggleFilter}
    />
  );
};

export const UnconnectedFilterMenu = ({
  classicRewardsCount,
  depositPayCount,
  freeCancellationCount,
  filters,
  isLoading,
  onToggleFilter,
  filterMessage,
}) => (
  <Fragment>
    <Flex>
      <PillButton
        onClick={() => onToggleFilter('freeCancellation')}
        selected={filters.freeCancellation}
        disabled={isLoading || !freeCancellationCount}
        data-testid="free-cancellation-button"
      >
        <Text>Free cancellation ({freeCancellationCount})</Text>
      </PillButton>
      <PillButton
        onClick={() => onToggleFilter('depositPay')}
        selected={filters.depositPay}
        disabled={isLoading || filters.classicRewards || (!filters.depositPay && !depositPayCount)}
        data-testid="deposit-pay-button"
      >
        <Text>Deposit Pay ({depositPayCount})</Text>
      </PillButton>
      <PillButton
        onClick={() => onToggleFilter('classicRewards')}
        selected={filters.classicRewards}
        disabled={isLoading || filters.depositPay || (!filters.classicRewards && !classicRewardsCount)}
        data-testid="classic-rewards-button"
      >
        <Text>Classic Rewards ({classicRewardsCount})</Text>
      </PillButton>
    </Flex>
    <FadeInWrapper alignItems="center" height={28} pt={4} visible={filterMessage ? 1 : 0}>
      <Text fontSize="xs" color="greys.steel" data-testid="filter-message">
        {filterMessage}
      </Text>
    </FadeInWrapper>
  </Fragment>
);

UnconnectedFilterMenu.propTypes = {
  classicRewardsCount: PropTypes.number.isRequired,
  depositPayCount: PropTypes.number.isRequired,
  filterMessage: PropTypes.string,
  filters: PropTypes.object.isRequired,
  freeCancellationCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onToggleFilter: PropTypes.func.isRequired,
};

UnconnectedFilterMenu.defaultProps = {
  filterMessage: null,
};

export default FilterMenu;
